// ============================================================
// THE COMPANY — KnowledgeMap Component
// ============================================================

import { useCompanyStore } from "../../store/useCompanyStore";

interface KnowledgeMapProps {
  roleId: string;
}

export default function KnowledgeMap({ roleId }: KnowledgeMapProps) {
  const decisions = useCompanyStore((s) => s.decisions);
  const roles = useCompanyStore((s) => s.roles);

  if (decisions.length === 0) {
    return (
      <section>
        <h3
          className="text-xs font-semibold uppercase tracking-wider mb-3"
          style={{ color: "var(--text-muted)" }}
        >
          Knowledge Map
        </h3>
        <div className="empty-state">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
          <p>No decisions tracked yet</p>
        </div>
      </section>
    );
  }

  return (
    <section>
      <h3
        className="text-xs font-semibold uppercase tracking-wider mb-3"
        style={{ color: "var(--text-muted)" }}
      >
        Knowledge Map
      </h3>

      <div className="space-y-2">
        {decisions.map((decision) => {
          const isInformed = decision.informedRoleIds.includes(roleId);
          const maker = roles.find((r) => r.id === decision.roleId);
          const dateStr = new Date(decision.createdAt).toLocaleDateString(
            undefined,
            {
              month: "short",
              day: "numeric",
            },
          );

          // Truncate decision content for display
          const truncated =
            decision.content.length > 80
              ? decision.content.slice(0, 80) + "..."
              : decision.content;

          return (
            <div
              key={decision.id}
              className="flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-sm"
              style={{
                backgroundColor: "var(--bg-elevated)",
                border: "1px solid var(--border-subtle)",
              }}
            >
              {/* Awareness indicator */}
              <span className="mt-0.5 shrink-0 text-base leading-none">
                {isInformed ? (
                  <span style={{ color: "var(--status-active)" }}>
                    &#10003;
                  </span>
                ) : (
                  <span style={{ color: "var(--status-blocked)" }}>
                    &#10005;
                  </span>
                )}
              </span>

              <div className="min-w-0 flex-1">
                <p
                  className="text-sm leading-snug"
                  style={{ color: "var(--text-primary)" }}
                >
                  {truncated}
                </p>
                <p
                  className="text-[11px] mt-1"
                  style={{ color: "var(--text-muted)" }}
                >
                  {maker ? maker.name : "Unknown"} &middot; {dateStr}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
